import { useAtom } from 'jotai';
import { useEffect, useCallback, useState } from 'react';
import { featureFlagsAtom, featureFlagsLoadingAtom, featureFlagsErrorAtom, featureFlagsCacheAtom, isCacheValidAtom, } from '../useFeatureFlag/atom';
import { useGetFeatureFlagsQuery } from '../services/featureFlagsService';
import { config } from '@ibk/shared-config';
export const useFeatureFlagsLoader = (options = {}) => {
    const { autoLoad = true, cacheEnabled = true, backgroundRefresh = false, staleWhileRevalidate = false, } = options;
    const [featureFlags, setFeatureFlags] = useAtom(featureFlagsAtom);
    const [isLoading, setIsLoading] = useAtom(featureFlagsLoadingAtom);
    const [error, setError] = useAtom(featureFlagsErrorAtom);
    const [cache, setCache] = useAtom(featureFlagsCacheAtom);
    const [isCacheValid] = useAtom(isCacheValidAtom);
    const [isStale, setIsStale] = useState(false);
    const { data: rtqData, isLoading: rtqLoading, error: rtqError, refetch, } = useGetFeatureFlagsQuery();
    const cacheTtl = parseInt(config.featureFlagsCacheTtl, 10);
    const debugMode = config.featureFlagsDebugMode === 'true';
    const loadFromServer = useCallback(async (forceRefresh = false) => {
        try {
            if (cacheEnabled && isCacheValid && !forceRefresh && cache?.data) {
                return cache.data;
            }
            const result = await refetch();
            if (result.data) {
                if (cacheEnabled) {
                    const now = Date.now();
                    setCache({
                        data: result.data,
                        timestamp: now,
                        ttl: cacheTtl,
                    });
                }
                setFeatureFlags(result.data);
                setIsStale(false);
                return result.data;
            }
            else {
                throw new Error('Data not found');
            }
        }
        catch (err) {
            const errorMessage = err.message || 'Error loading feature flags';
            setError(errorMessage);
            if (cacheEnabled && cache?.data) {
                setIsStale(true);
                return cache.data;
            }
            return null;
        }
    }, [cacheEnabled, isCacheValid, cache, cacheTtl, refetch, setCache, setFeatureFlags, setError]);
    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            await loadFromServer(false);
        }
        finally {
            setIsLoading(false);
        }
    }, [loadFromServer, setIsLoading]);
    const reload = useCallback(async () => {
        setIsLoading(true);
        try {
            await loadFromServer(true);
        }
        finally {
            setIsLoading(false);
        }
    }, [loadFromServer, setIsLoading]);
    const clearCache = useCallback(() => {
        setCache(null);
        setFeatureFlags(null);
        setIsStale(false);
    }, [setCache, setFeatureFlags]);
    useEffect(() => {
        if (!backgroundRefresh || !cache?.data)
            return;
        const interval = setInterval(() => {
            if (!isLoading) {
                loadFromServer(true).catch((error) => {
                    if (debugMode) {
                        console.warn('[useFeatureFlagsLoader] Background refresh failed:', error);
                    }
                });
            }
        }, cacheTtl);
        return () => clearInterval(interval);
    }, [backgroundRefresh, cache, cacheTtl, isLoading, loadFromServer]);
    useEffect(() => {
        if (!staleWhileRevalidate || !cache?.data)
            return;
        const now = Date.now();
        const isStaleData = now - cache.timestamp > cacheTtl / 2;
        if (isStaleData && !isLoading) {
            setIsStale(true);
            loadFromServer(true).catch((error) => {
                if (debugMode) {
                    console.warn('[useFeatureFlagsLoader] Stale revalidation failed:', error);
                }
            });
        }
    }, [staleWhileRevalidate, cache, cacheTtl, isLoading, loadFromServer]);
    useEffect(() => {
        if (autoLoad && !featureFlags && !rtqData && !isLoading && !rtqLoading) {
            load();
        }
    }, [autoLoad, featureFlags, rtqData, isLoading, rtqLoading, load]);
    useEffect(() => {
        if (rtqData && !featureFlags) {
            setFeatureFlags(rtqData);
        }
    }, [rtqData, featureFlags, setFeatureFlags]);
    useEffect(() => {
        if (rtqError && !error) {
            setError(rtqError.toString());
        }
    }, [rtqError, error, setError]);
    useEffect(() => {
        setIsLoading(rtqLoading);
    }, [rtqLoading, setIsLoading]);
    return {
        data: featureFlags || rtqData || null,
        isLoading: isLoading || rtqLoading,
        error: error || (rtqError ? rtqError.toString() : null),
        isStale,
        load,
        reload,
        clearCache,
    };
};
